import { Dispatch, memo, useCallback, useContext, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { ARRAY_ERROR } from 'final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { getRequirementsWithNonFixedOrApprovingViolations } from 'KnmErknmPackage/ErknmViolationsModule/common/getRequirementsWithNonFixedOrApprovingViolations';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { WAS_REMOVE_DUPLICATE_VIOLATIONS } from 'CorePackage/MainModule/constants/notices';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { IGeneralResultDecision } from 'KnmErknmPackage/ErkmAgreementsModule/interfaces/IGeneralResultDecision';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getRequirementsWithNonFixedOrApprovingOrSuspendedOrResolvedViolations } from 'KnmErknmPackage/ErknmViolationsModule/common/getRequirementsWithNonFixedOrApprovingOrSuspendedOrResolvedViolations';

import styles from './AgreementsRequirements.module.scss';
import { SelectViolationModal } from './SelectViolationModal/SelectViolationModal';

interface IAgreementsRequirementsProps {
  id: string;
  name: string;
  watchOnly: boolean;
  resultDecisions: IGeneralResultDecision[] | undefined;
  agreement: IErknmFormAgreement;
  agreementIndex: number;
}

interface IAgreementsRequirementsStoreProps {
  formRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const AgreementsRequirementsWithoutStore = memo(
  ({
    id,
    name,
    watchOnly,
    resultDecisions,
    agreement,
    formRef,
    decisionFormRef,
    agreementIndex,
  }: IAgreementsRequirementsProps & IAgreementsRequirementsStoreProps) => {
    const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
    const [isRequirementsNpaModalVisible, setIsRequirementsNpaModalVisible] = useState(false);
    const push = (isDecision ? decisionFormRef : formRef)?.current.mutators.push;
    const remove = (isDecision ? decisionFormRef : formRef)?.current.mutators.remove;
    const dispatch = useDispatch<Dispatch<RootActions>>();
    const requirementsNpaFieldName = `${name}.violations`;

    /**
     * Берутся только те требования, которые не были исправлены из выбранного предписания
     */
    const requirementsNpa = useMemo(() => {
      let requirements: IKnmErknmFormViolatedRequirementsNpa[] = [];

      const resultDecision = resultDecisions?.find(rd => rd.guid === agreement.prescription?.value);

      resultDecision?.violatedMandatoryRequirements?.forEach(vmr => {
        if (vmr) {
          requirements = requirements.concat(vmr);
        }
      });

      return getRequirementsWithNonFixedOrApprovingViolations(requirements);
    }, [resultDecisions, agreement]);

    /**
     * Берутся только те требования, которые не были исправлены из выбранного предписания или приостановлены по соглашению
     */
    const requirementsNpaWithStopped = useMemo(() => {
      let requirements: IKnmErknmFormViolatedRequirementsNpa[] = [];

      const resultDecision = resultDecisions?.find(rd => rd.guid === agreement.prescription?.value);

      resultDecision?.violatedMandatoryRequirements?.forEach(vmr => {
        if (vmr) {
          requirements = requirements.concat(vmr);
        }
      });

      return getRequirementsWithNonFixedOrApprovingOrSuspendedOrResolvedViolations(requirements);
    }, [resultDecisions, agreement]);

    /**
     * Функция добавления нарушения в соглашение
     */
    const addRequirementsNpa = useCallback(
      (items: IKnmErknmFormViolatedRequirementsNpa[], withoutMessage?: boolean) => {
        let wasFoundDuplicate = false;

        if (push) {
          items.forEach(item => {
            const npaIndex = !agreement.violations
              ? -1
              : agreement.violations.findIndex(
                  req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
                );

            if (npaIndex > -1) {
              item.violatedMandatory.forEach(ivm => {
                const vmIndex = agreement.violations?.[npaIndex].violatedMandatory.findIndex(
                  vm => vm.mandatory.id === ivm.mandatory.id,
                );

                if (vmIndex !== undefined && vmIndex > -1) {
                  ivm.violations?.forEach(iv => {
                    const violationIndex = agreement.violations?.[npaIndex].violatedMandatory[
                      vmIndex
                    ].violations?.findIndex(v => v.id === iv.id);

                    if (violationIndex !== undefined && violationIndex > -1) {
                      wasFoundDuplicate = true;
                    } else {
                      push(`${requirementsNpaFieldName}.${npaIndex}.violatedMandatory.${vmIndex}.violations`, iv);
                    }
                  });
                } else {
                  push(`${requirementsNpaFieldName}.${npaIndex}.violatedMandatory`, ivm);
                }
              });
            } else {
              push(requirementsNpaFieldName, item);
            }
          });
        }

        if (wasFoundDuplicate && !withoutMessage) {
          dispatch(
            addNoticeAction({
              renderContent: WAS_REMOVE_DUPLICATE_VIOLATIONS,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.NPA_CHECK,
            }),
          );
        }
      },
      [name, agreement, push],
    );

    /**
     * Функция удаления нарушения из соглашения
     */
    const removeRequirementsNpa = (npaIndex: number) => {
      remove && remove(requirementsNpaFieldName, npaIndex);
    };

    /**
     * Функция валидации самого массива, здесь проверяется что сам массив не пустой
     * и возвращает строку, валидация значений массива будет вынесена в общие валдицаии
     */
    const validateArray = useCallback((requirements: IKnmErknmFormViolatedRequirementsNpa[] | undefined) => {
      if (!requirements || !requirements.length) {
        return REQUIRED_ERROR;
      }

      return undefined;
    }, []);

    /**
     * Переменная для определения блокировки кнопки "Добавить"
     */
    const isAddButtonDisabled = useMemo(() => {
      let countViolationsInPrescription = 0;
      let countViolationsInAgreement = 0;

      requirementsNpa.forEach(r => {
        r.violatedMandatory.forEach(vm => {
          countViolationsInPrescription += vm.violations?.length || 0;
        });
      });

      agreement.violations?.forEach(v => {
        v.violatedMandatory.forEach(vm => {
          countViolationsInAgreement += vm.violations?.length || 0;
        });
      });

      return watchOnly || !agreement.prescription || countViolationsInPrescription === countViolationsInAgreement;
    }, [watchOnly, agreement, requirementsNpa]);

    return (
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Нарушения, включенные в соглашение',
          field: requirementsNpaFieldName,
          isKnm,
          isPm,
          isDecision,
        })}
        className={styles.Wrapper}
        id={`${id}.violations`}
        button={
          <>
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={isAddButtonDisabled}
              onClick={() => setIsRequirementsNpaModalVisible(true)}
            />
            <Button
              className="shared-btn-add"
              text="Добавить все нарушения"
              type="button"
              variant="link"
              color="primary"
              disabled={isAddButtonDisabled}
              onClick={() => addRequirementsNpa(requirementsNpa, true)}
            />
          </>
        }
      >
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            const fieldError = props.errors?.agreements?.[agreementIndex]?.violations;
            const isFieldTouched = !!props.touched?.[`agreements[${agreementIndex}].violations`];

            if (isFieldTouched && fieldError?.[ARRAY_ERROR]) {
              return (
                <p className={`shared-input-error shared-row__small-margin ${styles.ArrayErrorMessage}`}>
                  Поле не может быть пустым
                </p>
              );
            }

            return null;
          }}
        </FormSpy>
        <FieldArray name={requirementsNpaFieldName} validate={validateArray}>
          {({ fields }) =>
            fields.map((npaName = name, index) => {
              const fieldValue: IKnmErknmFormViolatedRequirementsNpa = fields.value?.[index];
              const correspondingRequirement = requirementsNpaWithStopped.find(
                requirement => requirement.npa.id === fieldValue.npa.id,
              );

              if (!correspondingRequirement) {
                return null;
              }

              return (
                <Field key={npaName} name={npaName}>
                  {({ meta }) => {
                    const isFieldTouched = meta.touched;
                    const errorField = meta.error;

                    return (
                      <KnmViolatedRequirementsNpaItem
                        value={fieldValue}
                        name={npaName}
                        watchOnly={watchOnly}
                        isViolationFieldsWatchOnly={watchOnly}
                        onRemove={() => removeRequirementsNpa(index)}
                        addMandatory={() => undefined}
                        touched={isFieldTouched}
                        error={errorField}
                        formRef={isDecision ? decisionFormRef : formRef}
                        isMandatoryDisabled={true}
                        isInsideAgreementsSection
                        actRequirement={correspondingRequirement}
                        isPaperless={true}
                        id={`${id}.violations[${fieldValue.guid || index}]`}
                        isKnm={isKnm}
                        isPm={isPm}
                        isDecision={isDecision}
                      />
                    );
                  }}
                </Field>
              );
            })
          }
        </FieldArray>
        <SelectViolationModal
          requirementsNpa={requirementsNpa}
          visible={isRequirementsNpaModalVisible}
          onClose={() => setIsRequirementsNpaModalVisible(false)}
          onSubmit={addRequirementsNpa}
        />
      </FieldBlock>
    );
  },
);

const mapStateToProps = (state: IRootState): IAgreementsRequirementsStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const AgreementsRequirements = connect(mapStateToProps)(AgreementsRequirementsWithoutStore);
